import unitHelper from '@client/helper/unit';
import { i18n } from '@client/helper/i18n.js';
import { CHART_TYPE_AREA, CHART_TYPE_FILL_AREA, CHART_TYPE_MULTI_COLOR_FILL_AREA } from '@client/pages/activity-detail/config/chart.types';

import { intensityLapData } from '@client/pages/activity-detail/hooks/useActivityDetail';
import { gradingGraph } from '@client/pages/activity-detail/main/chart/hooks/useGrading';
import { FREQUENCY_LIST, IMPERIAL_UNITS, LAP_LIST, METRIC_UNITS, NULL_TEXT } from '../common';
import * as corosSportConfig from '@coros/sport-config';
import { IChartRenderConfig } from './types';
import type { IGradingSystemDataItem, IndoorClimbTrainingSportType, SystemType } from '@coros/sport-config';
import { UnitType } from '@mytypes/activity-detail/Common';

const { getGradingGrade, CLIMB_STYLE, INDOOR_CLIMB_BOULDERING_CHART_NAME } = corosSportConfig.sport.climb;

const { t } = i18n;

type IProps = {
  getFrom: string;
  chartType: string;
  sportType: number;
  unitType: UnitType;
};

export function getClimbConfig({ getFrom, chartType, unitType, sportType }: IProps): Record<string, IChartRenderConfig> {
  if (![800, 801, 6, 7, 105, 106].includes(sportType)) return {};

  return {
    // 海拔
    ElevationForIndoorClimbBouldering: {
      text: t('H5029'),
      unit: {
        [METRIC_UNITS]: 'm',
        [IMPERIAL_UNITS]: 'ft'
      }[unitType],
      graphObjKey: 'altitude',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'altitude',
          [LAP_LIST]: 'altitude'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_AREA,
      color: 'rgba(102, 204, 255, 1)',
      tickText: (value: number, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return unitHelper.distanceM2ft(value / 10, 1, unitType);
      }
    },
    // 难度
    Grade: {
      text: t('H5046'),
      // chartType: CHART_TYPE_AREA,
      chartType: CHART_TYPE_FILL_AREA,
      color: 'rgba(0, 179, 255, 0.9)',
      areaColor: [
        {
          offset: '0%',
          color: 'rgba(0, 179, 255, 0.9)'
        },
        {
          offset: '100%',
          color: 'rgba(0, 218, 255, 0.1)'
        }
      ],
      unit: '',
      graphObjKey: '',
      valueKey: (() => {
        return {
          // 暂无
          [FREQUENCY_LIST]: NULL_TEXT,
          [LAP_LIST]: NULL_TEXT
        }[getFrom];
      })(),
      valueFunction(d, i) {
        let result = '';
        if (chartType === 'index-rect') {
          result = getGradingGrade(false, d.gradingSystem, d.hardestGrade);

          return result.commonGrade[result.commonGrade.length - 1];
        }

        for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
          const item = intensityLapData.value[n];
          if (item.startTimestamp <= d.timestamp && d.timestamp <= item.endTimestamp) {
            const gradingGrade = getGradingGrade(false, item.gradingSystem, item.hardestGrade);

            if (gradingGrade) {
              result = gradingGrade;

              break;
            }
          }
        }

        if (!result) {
          return '';
        }
        return result.commonGrade[result.commonGrade.length - 1];
      },
      tickText: (value, d) => {
        if (!d) {
          return value;
        }

        let result = '';

        if (chartType === 'index-rect') {
          result = getGradingGrade(false, d.gradingSystem, d.hardestGrade);
        } else {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (item.startTimestamp <= d.timestamp && d.timestamp <= item.endTimestamp) {
              result = getGradingGrade(false, item.gradingSystem, item.hardestGrade);
              break;
            }
          }
        }

        if (!result) {
          return NULL_TEXT;
        }

        return result.gradeNameI18n ? t(result.gradeNameI18n) : result.gradeName;
      },
      useIt() {
        return !!gradingGraph.value;
      }
    },
    // 难度-室内攀岩 IndoorClimbGrade
    [INDOOR_CLIMB_BOULDERING_CHART_NAME]: {
      text: t('H5046'),
      chartType: CHART_TYPE_MULTI_COLOR_FILL_AREA,
      color: '#00AAFF',
      multiAreaColors: CLIMB_STYLE.reduce((acc, item) => {
        acc['style' + item.value] = {
          value: item.value,
          nochart: item.nochart,
          legend: {
            color: item.color1,
            label: t(item.i18n)
          },
          colors: [
            { offset: '0%', color: item.color1 },
            { offset: '100%', color: item.color2 }
          ]
        };
        return acc;
      }, {}),
      unit: '',
      graphObjKey: '',
      valueKey: (() => {
        return {
          // 暂无
          [FREQUENCY_LIST]: NULL_TEXT,
          [LAP_LIST]: NULL_TEXT
        }[getFrom];
      })(),
      valueFunction(d, index, args) {
        let result = '';

        let findGradingGrade: IGradingSystemDataItem | null = null;
        if (chartType === 'index-rect') {
          findGradingGrade = getGradingGrade(sportType as IndoorClimbTrainingSportType, d.gradingSystem, d.hardestGrade);

          return findGradingGrade ? findGradingGrade.commonGrade[findGradingGrade.commonGrade.length - 1] : '';
        }

        for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
          const item = intensityLapData.value[n];

          // 快速跳过格式不对的数据
          if (!item.gradingSystem || item.hardestGrade === 255) continue;

          if (item.startTimestamp <= d.timestamp && d.timestamp <= item.endTimestamp) {
            // 要求同一个完攀值
            if (args && args.multiAreaIndex && args.multiAreaIndex !== item.style) {
              return;
            }

            // if (index === 234) {
            //   console.log(`findGradingGrade`, index, sportType, item.gradingSystem, item.hardestGrade);
            // }
            const gradingGrade = getGradingGrade(
              sportType as IndoorClimbTrainingSportType,
              item.gradingSystem as SystemType,
              item.hardestGrade
            );

            if (gradingGrade) {
              findGradingGrade = gradingGrade;

              break;
            }
          }
        }

        if (!findGradingGrade) {
          return '';
        }
        return findGradingGrade.commonGrade[findGradingGrade.commonGrade.length - 1];
      },
      tickText: (value, d) => {
        if (!d) {
          return value;
        }

        let result = '';

        if (chartType === 'index-rect') {
          result = getGradingGrade(sportType as IndoorClimbTrainingSportType, d.gradingSystem, d.hardestGrade);
        } else {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];

            // 快速跳过格式不对的数据
            if (!item.gradingSystem || item.hardestGrade === 255) continue;

            if (item.startTimestamp <= d.timestamp && d.timestamp <= item.endTimestamp) {
              result = getGradingGrade(sportType as IndoorClimbTrainingSportType, item.gradingSystem as SystemType, item.hardestGrade);
              break;
            }
          }
        }

        if (!result) {
          return NULL_TEXT;
        }

        return result.gradeNameI18n ? t(result.gradeNameI18n) : result.gradeName;
      },
      useIt() {
        console.log(`useit`, gradingGraph.value);
        return !!gradingGraph.value;
      }
    }
  };
}
